import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Search, Wand2 } from "lucide-react";
import Modal from "../ui/Modal";
import { Badge } from "../ui/Badge";
import { cn } from "../../utils/cn";
import { toast } from "../../utils/toast";
import { workflowErrorMessage } from "./workflowErrorMessage";
import { getMacros, runMacro } from "../../services/helpdeskWorkflowService";
import useHelpdeskLabels from "./useHelpdeskLabels";

// ─── Macros en el detalle ────────────────────────────────────────────────────
// Popover buscable con las macros del usuario (globales + personales). Las que
// le escriben al contacto (`send_message`) o cierran el caso (`resolve`) piden
// confirmación mostrando sus pasos: son las dos acciones que no se deshacen
// con otro clic. El resto corre directo. El resultado (qué paso falló y por
// qué) va a un aviso.
//
// El estado de apertura lo controla el detalle: en escritorio se abre con el
// botón del header, en móvil desde el menú "Más acciones".

// Nombre corto de cada tipo de acción (aviso de fallo, lista de pasos).
export const MACRO_ACTION_NAMES = {
  add_labels: "Agregar etiquetas",
  remove_labels: "Quitar etiquetas",
  assign: "Asignar",
  set_folder: "Mover a carpeta",
  add_note: "Nota privada",
  send_message: "Enviar mensaje",
  resolve: "Cerrar caso",
};

const CONFIRM_TYPES = new Set(["send_message", "resolve"]);
const PREVIEW_MAX = 80;

const clip = (text) => {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  return s.length > PREVIEW_MAX ? `${s.slice(0, PREVIEW_MAX - 1)}…` : s;
};

export function macroNeedsConfirm(macro) {
  return (macro?.actions || []).some((a) => CONFIRM_TYPES.has(a?.type));
}

/**
 * Frase legible de un paso de macro. Exportada para test.
 * @param {{type:string, params?:object}} action
 * @param {{t:Function, labelsById?:object, foldersById?:object, getDisplayName?:Function}} ctx
 */
export function describeMacroStep(action, { t, labelsById = {}, foldersById = {}, getDisplayName }) {
  const p = action?.params || {};
  const labelNames = (ids) =>
    (Array.isArray(ids) ? ids : [])
      .map((id) => labelsById[id]?.name)
      .filter(Boolean)
      .join(", ") || t("(etiqueta eliminada)");
  switch (action?.type) {
    case "add_labels":
      return t("Agregar etiquetas: {{etiquetas}}").replace("{{etiquetas}}", labelNames(p.label_ids));
    case "remove_labels":
      return t("Quitar etiquetas: {{etiquetas}}").replace("{{etiquetas}}", labelNames(p.label_ids));
    case "assign": {
      if (p.agent_id === "self") return t("Asignarme el caso");
      const name = p.agent_id && getDisplayName ? getDisplayName(p.agent_id) : "";
      return name && name !== p.agent_id
        ? t("Asignar a {{agente}}").replace("{{agente}}", name)
        : t("Asignar a otro agente");
    }
    case "set_folder":
      if (!p.folder_id) return t("Quitar de la carpeta");
      return t("Mover a la carpeta {{carpeta}}").replace(
        "{{carpeta}}",
        foldersById[p.folder_id]?.name || t("(carpeta eliminada)"),
      );
    case "add_note":
      return t("Nota privada: «{{texto}}»").replace("{{texto}}", clip(p.content));
    case "send_message":
      return t("Enviar al contacto: «{{texto}}»").replace("{{texto}}", clip(p.content));
    case "resolve":
      return p.note
        ? t("Cerrar el caso con la nota «{{texto}}»").replace("{{texto}}", clip(p.note))
        : t("Cerrar el caso");
    default:
      return action?.type || "—";
  }
}

/**
 * Texto del aviso tras correr una macro. Exportada para test.
 * @returns {{ok:boolean, text:string}}
 */
export function macroResultSummary(macro, result, t) {
  const results = Array.isArray(result?.results) ? result.results : [];
  const failedIndex = results.findIndex((r) => r && r.ok === false);
  const name = macro?.name || t("Macro");
  if (failedIndex === -1 && result?.ok !== false) {
    return {
      ok: true,
      text: t("Macro «{{nombre}}» aplicada ({{n}} pasos).")
        .replace("{{nombre}}", name)
        .replace("{{n}}", String(results.length || macro?.actions?.length || 0)),
    };
  }
  const failed = results[failedIndex] || {};
  const stepName = MACRO_ACTION_NAMES[failed.type] ? t(MACRO_ACTION_NAMES[failed.type]) : failed.type || "?";
  const text = t("La macro «{{nombre}}» se detuvo en el paso {{n}} ({{paso}})")
    .replace("{{nombre}}", name)
    .replace("{{n}}", String(failedIndex >= 0 ? failedIndex + 1 : "?"))
    .replace("{{paso}}", stepName);
  const done = failedIndex > 0 ? ` ${t("Los pasos anteriores sí se aplicaron.")}` : "";
  return { ok: false, text: `${text}${failed.error ? `: ${failed.error}` : "."}${done}` };
}

const macroIdOf = (m) => m?.id || m?._id;

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {object}  props.conversation
 * @param {object}  [props.foldersById]
 * @param {Function} [props.getDisplayName]
 * @param {(conv:object) => void} [props.onUpdated]   conversación devuelta por el run
 * @param {() => void} [props.onRefresh]              relee mensajes tras el run
 * @param {object}  [props.anchorRef]                 botón que abre el popover
 * @param {(busy:boolean) => void} [props.onBusyChange] macro corriendo (el
 *   detalle deshabilita sus acciones mientras tanto)
 */
export default function MacrosControl({
  open,
  onClose,
  conversation,
  foldersById,
  getDisplayName,
  onUpdated,
  onRefresh,
  anchorRef,
  onBusyChange,
}) {
  const { t } = useTranslation();
  const { byId: labelsById } = useHelpdeskLabels();
  const [macros, setMacros] = useState(null); // null = cargando
  const [loadError, setLoadError] = useState(null);
  const [query, setQuery] = useState("");
  const [confirming, setConfirming] = useState(null); // macro a confirmar
  const [runningId, setRunningId] = useState(null);
  const rootRef = useRef(null);
  const conversationId = conversation?.id;

  // `t` por ref: el efecto de carga depende solo de la apertura (si dependiera
  // de `t`, un cambio de idioma con el popover abierto lo recargaría).
  const tRef = useRef(t);
  useEffect(() => {
    tRef.current = t;
  });
  const load = useCallback(async () => {
    setMacros(null);
    setLoadError(null);
    try {
      const list = await getMacros();
      setMacros(Array.isArray(list) ? list : []);
    } catch (err) {
      setMacros([]);
      setLoadError(workflowErrorMessage(err, tRef.current("No se pudieron cargar las macros.")));
    }
  }, []);

  // Se piden en cada apertura: son pocas y otro supervisor puede haberlas editado.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return undefined;
    const handleClick = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      if (anchorRef?.current?.contains(e.target)) return;
      onClose?.();
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, onClose, anchorRef]);

  const visible = useMemo(() => {
    if (!macros) return [];
    const q = query.trim().toLowerCase();
    const list = q ? macros.filter((m) => String(m.name || "").toLowerCase().includes(q)) : macros;
    return [...list].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [macros, query]);

  const execute = useCallback(
    async (macro) => {
      const id = macroIdOf(macro);
      if (!id || !conversationId) return;
      setRunningId(id);
      onBusyChange?.(true);
      try {
        const result = await runMacro(conversationId, id);
        const summary = macroResultSummary(macro, result, t);
        if (summary.ok) toast.success(summary.text);
        else toast.error(summary.text);
        if (result?.conversation?.id) onUpdated?.(result.conversation);
      } catch (err) {
        toast.error(workflowErrorMessage(err, t("No se pudo ejecutar la macro.")));
      } finally {
        setRunningId(null);
        setConfirming(null);
        onBusyChange?.(false);
        // Aunque falle a mitad, los pasos previos pudieron escribir: se relee.
        onRefresh?.();
      }
    },
    [conversationId, onUpdated, onRefresh, onBusyChange, t],
  );

  const pick = (macro) => {
    onClose?.();
    if (macroNeedsConfirm(macro)) setConfirming(macro);
    else execute(macro);
  };

  const stepCtx = { t, labelsById, foldersById, getDisplayName };

  return (
    <>
      {open && (
        <div
          ref={rootRef}
          role="dialog"
          aria-label={t("Macros")}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              e.stopPropagation();
              onClose?.();
              // Con teclado, el foco vuelve al botón que abrió el panel.
              anchorRef?.current?.focus?.();
            }
          }}
          className="absolute right-0 top-full mt-1 z-30 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-bg shadow-elevated animate-scale-in origin-top-right motion-reduce:animate-none"
        >
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search
                className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-muted pointer-events-none"
                aria-hidden="true"
              />
              <input
                // type="search", not "text": the legacy global rule
                // `input[type="text"]` (_modern-forms.scss) overrides pl-8 and the
                // placeholder ran under the icon. The native clear button is hidden;
                // max-md:text-[16px] keeps the 16px that rule gave phones (no iOS zoom).
                type="search"
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("Buscar macro…")}
                aria-label={t("Buscar macro")}
                className="w-full h-8 pl-8 pr-3 rounded-md border border-border bg-bg text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-brand-500 max-md:text-[16px] [&::-webkit-search-cancel-button]:appearance-none"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto py-1" role="list">
            {macros === null ? (
              <div className="px-3 py-2 space-y-2" aria-hidden="true">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="space-y-1">
                    <div className="h-3 w-1/2 rounded bg-bg-muted animate-pulse" />
                    <div className="h-2.5 w-1/4 rounded bg-bg-muted animate-pulse" />
                  </div>
                ))}
              </div>
            ) : loadError ? (
              <div className="px-3 py-4 text-center">
                <div className="flex items-center justify-center gap-1.5 text-xs text-status-error">
                  <AlertTriangle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
                  {loadError}
                </div>
                <button
                  type="button"
                  onClick={load}
                  className="mt-2 h-8 px-3 rounded-md text-xs font-medium text-brand-700 hover:bg-brand-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
                >
                  {t("Reintentar")}
                </button>
              </div>
            ) : macros.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-fg-muted">
                {t("Aún no hay macros. Créalas en los ajustes del helpdesk.")}
              </div>
            ) : visible.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-fg-muted">
                {t("Sin resultados para la búsqueda.")}
              </div>
            ) : (
              visible.map((macro) => {
                const id = macroIdOf(macro);
                const steps = macro.actions?.length || 0;
                return (
                  <div role="listitem" key={id}>
                    <button
                      type="button"
                      onClick={() => pick(macro)}
                      disabled={!!runningId}
                      className="w-full min-h-[44px] flex items-center gap-2 px-3 py-2 text-left hover:bg-bg-muted focus:outline-none focus-visible:bg-bg-muted disabled:opacity-50 transition-colors"
                    >
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm text-fg truncate">{macro.name}</span>
                        <span className="block text-[11px] text-fg-muted truncate">
                          {(steps === 1 ? t("1 paso") : t("{{n}} pasos").replace("{{n}}", String(steps)))}
                          {macroNeedsConfirm(macro) && ` · ${t("pide confirmación")}`}
                        </span>
                      </span>
                      {macro.visibility === "personal" && (
                        <Badge tone="neutral" className="text-[11px] shrink-0">
                          {t("Personal")}
                        </Badge>
                      )}
                    </button>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <Modal
        open={!!confirming}
        onClose={() => (runningId ? null : setConfirming(null))}
        title={t("¿Ejecutar la macro «{{nombre}}»?").replace("{{nombre}}", confirming?.name || "")}
        description={t("Esta macro le escribe al contacto o cierra el caso. Revisa los pasos:")}
        size="md"
        footer={
          <>
            <button
              type="button"
              onClick={() => setConfirming(null)}
              disabled={!!runningId}
              className="h-9 px-3 rounded-md text-sm font-medium text-fg hover:bg-bg-muted disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
            >
              {t("Cancelar")}
            </button>
            <button
              type="button"
              onClick={() => confirming && execute(confirming)}
              disabled={!!runningId}
              className="hd-brand-solid inline-flex items-center gap-2 h-9 px-3 rounded-md text-sm font-medium bg-brand-500 text-bg hover:bg-brand-600 active:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 transition-colors"
            >
              {runningId && (
                <span className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin motion-reduce:animate-none" />
              )}
              {t("Ejecutar macro")}
            </button>
          </>
        }
      >
        <ol className="space-y-1.5 text-sm text-fg">
          {(confirming?.actions || []).map((action, i) => (
            <li key={i} className="flex items-start gap-2">
              <span
                className={cn(
                  "inline-flex w-5 h-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold tabular-nums",
                  CONFIRM_TYPES.has(action.type)
                    ? "bg-status-warning-soft text-status-warning"
                    : "bg-bg-muted text-fg-muted",
                )}
                aria-hidden="true"
              >
                {i + 1}
              </span>
              <span className="min-w-0 break-words">{describeMacroStep(action, stepCtx)}</span>
            </li>
          ))}
        </ol>
      </Modal>
    </>
  );
}

// Botón del header (escritorio). Vive acá para que el ícono y el rótulo de las
// macros estén en un solo lugar.
export const MacrosButton = React.forwardRef(function MacrosButton({ open, onClick, disabled }, ref) {
  const { t } = useTranslation();
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-haspopup="dialog"
      aria-expanded={open}
      aria-label={t("Macros")}
      title={t("Macros")}
      className={cn(
        "inline-flex w-11 h-11 md:w-8 md:h-8 items-center justify-center rounded-md transition-colors disabled:opacity-50",
        open ? "bg-bg-subtle text-fg" : "text-fg-muted hover:bg-bg-muted hover:text-fg",
      )}
    >
      <Wand2 className="w-4 h-4" aria-hidden="true" />
    </button>
  );
});
